import { copyStringToClipboard, formatDate } from '@/support/String';
import { Component, Vue, Watch } from 'vue-property-decorator';
import LoaderCard from '@/components/loader-card/LoaderCard';
import { Order, Invoice, PersonalNotes, Attachments, Label } from '@/models/Order';
import { AxiosError } from 'axios';
import ErrorHandler from '@/support/ErrorHandler';
import { styledBoxOptions, StyledBoxOptions } from '@/support/StyledBox';
import { formatDate as format, sortObjectsArrayByProperty } from '@/support/String';
import { TableVisibility } from '@/components/data-table/DataTable';
import { OrderItem } from '@/models/OrderItem';
import { SetItem } from '@/models/SetItem';
import { cloneDeep } from 'lodash';
import OrderStatusOptions, { StatusOptions } from "@/support/OrderStatus";
import { Options } from '@/components/mi-dialog/MiDialog';
import { DateTime } from 'luxon';
import OrderTable from '@/views/Webshop/OrderDetail/OrderTable';
import OrderRelatedSets from '@/views/Webshop/OrderDetail/OrderRelatedSets';
import { RoleEnum, User } from '@/models/User';
import EditPersonalNotesDialog from '@/views/Webshop/OrderDetail/EditPersonalNotesDialog';
import StyledBox from '@/support/StyledBoxType';

@Component<OrderDetail>({
  components: {
    LoaderCard,
    OrderTable,
    OrderRelatedSets,
    EditPersonalNotesDialog,
  },
  filters: {
    formatDate(date: string) {
      if (date === '0000-00-00 00:00:00' || !date) {
        return 'N/A'
      } else {
        return DateTime.fromSQL(date).toFormat('dd-LL-yyyy');
      }
    },
    formatDateTime(date: string) {
      if (date === '0000-00-00 00:00:00' || !date) {
        return 'N/A'
      } else {
        return DateTime.fromSQL(date).toFormat('dd-LL-yyyy HH:MM');
      }
    },
    formatDateLink(date: string) {
      if (date === '0000-00-00 00:00:00' || !date) {
        return 'N/A'
      } else {
        return DateTime.fromSQL(date).toFormat('ddLLyyyy');
      }
    },
    formatTime(time: string) {
      return !time ? '' : format(time, 'HH:mm');
    }
  }
})
export default class OrderDetail extends Vue {
  public $pageTitle = 'Order overview';
  protected order: Order = new Order();
  protected orderClone: Order | null = null;

  protected orderInvoices: Invoice[] | null = null;
  protected orderPersonalNotes: PersonalNotes[] | null = null;
  protected orderAttachments: Attachments[] | null = null;
  protected orderLabels: Label[] | null = null;
  protected sets: SetItem[] | null = null;
  protected stylists: User[] | null = null;
  protected note: any | null = null;

  protected styledBoxOptions: StyledBoxOptions[] = styledBoxOptions;

  protected isLoading: boolean = false;
  protected isEditingOrder: boolean = false;
  protected isUpdating: boolean = false;

  protected isCreatingLabel: boolean = false;
  protected isEditingPersonalNotes: boolean = false;
  protected isEditingEvaluation: boolean = false;
  protected isUpdatingEvaluation: boolean = false;

  protected selectedStatus: string = '';
  protected selectedStyledBox: string = '';
  protected selectedStylist: User | null = null;
  protected orderNote: string = '';
  protected finalReturnDatePicker: boolean = false;
  protected returnedAtDatePicker: boolean = false;

  protected invoiceStatus: string = '';
  protected activeTab: string = 'tab-1';
  protected orderItemsMap: OrderItemsMap = {}

  protected isSyncingPvx: boolean = false;

  protected visibility: TableVisibility = {
    search: false,
    total: false,
    limit: false,
    pagination: false,
    checkboxes: false
  };

  protected copyStringToClipboard: Function = copyStringToClipboard;

  protected orderItemCount: number | null = null;

  public mounted() {
    this.initialize();
  }

  protected async initialize(): Promise<void> {
    await this.fetchOrder();
  }

  protected async fetchOrder(): Promise<void> {
    this.isLoading = true;

    await new Order()
      .include(['user', 'order-items', 'product', 'brand', 'color', 'size', 'season', 'invoices', 'personal-notes', 'evaluation', 'attachments', 'labels', 'paid_at'])
      .find(this.$route.params.id)
      .then((order: Order) => {
        if (!order) {
          return;
        }

        this.order = order;
        this.orderInvoices = order.invoices as Invoice[];
        this.orderPersonalNotes = order.personal_notes as PersonalNotes[];
        this.orderAttachments = order.attachments ? order.attachments as Attachments[] : [];
        this.orderLabels = order.labels ? order.labels as Label[] : [];
        if (order.order_items) {
          this.orderItemCount = order.order_items.length;
        }

        if (order.note) this.orderNote = order.note;

        this.selectedStatus = order.status as string;
        this.selectedStyledBox = order.box_type as string;

        this.createOrderItemsMap();
        this.fetchUsers();

        this.isLoading = false;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected async syncPvx() {
    this.isSyncingPvx = true;
    await new Order().syncPvx(this.$route.params.id)
      .then((response: any) => {
        this.isSyncingPvx = false;
        this.initialize();
      })
      .catch((error: AxiosError) => {
        this.isSyncingPvx = false;
        ErrorHandler.network(error);
      });
  }

  protected fetchUsers() {
    new User()
      .filter({ 'role': 'stylist' })
      .limit(100)
      .sort('name', 'ASC')
      .all()
      .then((users: User[]) => {
        this.stylists = users;
        this.selectedStylist = users.find((user: User) => {
          return user.name === this.order.stylist_name;
        }) as User;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected update(status: string, type: string) {
    if (!this.order.id) {
      return;
    }

    const payloadStatus = {
      new_status: status,
      orders: [this.order.id],
    }

    new Order()
      .order(this.order.id)
      .updateStatusType(payloadStatus, 'status')
      .then(() => {
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });

    let payloadUpdate = {
      box_type: type,
      orders: [this.order.id],
      note: this.orderNote,
      stylist: this.selectedStylist ? this.selectedStylist.id : null,
      final_return_date: this.order.final_return_date,
      returned_at: this.order.returned_at,
    }

    new Order()
      .order(this.order.id)
      .update(payloadUpdate)
      .then((order: Order) => {
        this.$store.dispatch('openDialog', this.dialogSuccessOptions);
        this.isEditingOrder = false;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      })
  }

  protected updateEvaluation() {
    if (!this.order.id && !this.order.evaluation) {
      return;
    }

    let payload: any = {
      "q1_tevreden": this.order.evaluation.q1_tevreden,
      "q2_aanbevelen": this.order.evaluation.q2_aanbevelen,
      "q3_makkelijk": this.order.evaluation.q3_makkelijk,
      "q4_begrip": this.order.evaluation.q4_begrip,
      "q5_volgende": this.order.evaluation.q5_volgende,
      order_items: [],
    };

    let orderItemLoop = 0;

    if (this.order.order_items) {
      this.order.order_items.forEach((orderItem, index) => {
        payload.order_items.push({
          evaluation: {
            overal: orderItem.evaluation.overal,
            size: orderItem.evaluation.size,
            fit: this.orderItemsMap[orderItem.id as any].fit ? this.orderItemsMap[orderItem.id as any].fit.join(',') : '',
            style: this.orderItemsMap[orderItem.id as any].style ? this.orderItemsMap[orderItem.id as any].style.join(',') : '',
            other: this.orderItemsMap[orderItem.id as any].other ? this.orderItemsMap[orderItem.id as any].other.join(',') : '',
          },
          uuid: orderItem.id,
        });
        orderItemLoop++;
        if (orderItemLoop === (this.order.order_items as any).length) {
          this.updateOrderEvalation(payload);
        }
      });
    }
  }

  protected updateOrderEvalation(payload: any) {
    if (!this.order.id) {
      return;
    }

    new Order()
      .evaluateOrder(this.order.id, payload)
      .then((order: Order) => {
        this.$store.dispatch('openDialog', this.dialogSuccessOptions);
        this.isEditingEvaluation = false;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
        this.isEditingEvaluation = false;
      })
  }

  protected updateStatus(status: string) {
    if (!this.order.id) {
      return;
    }

    this.invoiceStatus = status;
    this.$store.dispatch('openDialog', this.dialogUpdateStatus);
  }

  protected async getShippingStatus(barcode: string) {
    if (!barcode) {
      return;
    }

    await new Order()
      .getShippingStatus(barcode)
      .then((data: any) => {
        if (!this.orderLabels) {
          return;
        }

        const orderLabels = this.orderLabels.filter((orderLabel: Label) => {
          return orderLabel.barcode === barcode;
        })

        orderLabels.forEach((orderLabel: Label) => {
          if (!data) {
            return;
          }

          orderLabel.status = data.status ? data.status : '-';
          orderLabel.sent_at = data.sent_at ? data.sent_at : '-';
          orderLabel.delivered_at = data.delivered_at ? data.delivered_at : '-';
        });

        this.$store.dispatch('openDialog', this.shippingStatus);
      }).catch(() => {
        this.$store.dispatch('openDialog', this.shippingStatusFailed);
      });
  }

  protected createInvoice() {
    const payload = {
      orders: [this.order.id],
    }

    new Order()
      .createInvoice(payload)
      .then(() => {
        this.invoiceStatus = 'aangemaakt';
        this.$store.dispatch('openDialog', this.invoiceStatusOption);
        this.initialize();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected deleteInvoice() {
    this.$store.dispatch('openDialog', this.deleteDialogOptions);
  }

  protected resetOrder() {
    if (!this.orderClone) {
      return;
    }

    this.order = cloneDeep(this.orderClone);
    this.orderClone = null;
  }

  protected updatePersonalNote(note: any) {
    this.note = note;
    this.$store.dispatch('openDialog', this.updateNoteDialogOptions);
  }

  protected createOrderItemsMap() {
    if (!this.order || !this.order.order_items) {
      return;
    }

    const orderItemsMap: OrderItemsMap = {}

    this.order.order_items.forEach((orderItem: OrderItem) => {
      const itemId: any = orderItem.id;

      orderItemsMap[itemId] = {
        fit: orderItem.evaluation.fit ? orderItem.evaluation.fit.split(',') : [],
        style: orderItem.evaluation.style ? orderItem.evaluation.style.split(',') : [],
        other: orderItem.evaluation.other ? orderItem.evaluation.other.split(',') : [],
      }
    });

    this.$set(this, 'orderItemsMap', orderItemsMap);
  }

  protected async createLabel(): Promise<void> {
    if (!this.order.id) {
      return;
    }

    this.isCreatingLabel = true;
    await new Order()
      .createLabel(this.order.id)
      .catch((error: AxiosError): null => {
        ErrorHandler.network(error);
        return null;
      });

    this.initialize();
    this.isCreatingLabel = false;
  }

  protected get styledBox() {
    const styledBox = styledBoxOptions.find((styledBox: StyledBoxOptions) => {
      return this.order.box_type === styledBox.value;
    });

    if (!styledBox) {
      return;
    }

    return (styledBox as StyledBoxOptions).text;
  }

  get invoiceStatusOption(): Options {
    const status = this.invoiceStatus;
    return {
      title: `Invoice ${status}`,
      text: `Invoice is ${status}`,
      type: 'success',
      buttons: {
        confirm: {
          text: 'Oke',
          action: () => {
            this.initialize();
          }
        },
      },
    };
  }

  get shippingStatus(): Options {
    return {
      title: `Shipping status opgehaald`,
      text: `Shipping status opgehaald`,
      type: 'success',
      buttons: {
        confirm: {
          text: 'Oke',
        },
      },
    };
  }

  get shippingStatusFailed(): Options {
    return {
      title: `Shipping status niet opgehaald`,
      text: `Shipping status niet opgehaald`,
      type: 'warning',
      buttons: {
        confirm: {
          text: 'Oke',
        },
      },
    };
  }

  get dialogSuccessOptions(): Options {
    return {
      title: 'Order update',
      text: 'Alles is opgeslagen',
      type: 'success',
      buttons: {
        confirm: {
          text: 'Oke',
          action: () => {
            this.initialize();
          }
        },
      },
    };
  }

  get dialogStatusOptions(): Options {
    return {
      title: 'Order update',
      text: `Order status is veranderd: <span style="text-decoration: line-through;">${this.order.status}</span> >> ${this.selectedStatus}`,
      type: 'success',
      buttons: {
        confirm: {
          text: 'Oke',
          action: () => {
            this.initialize();
          }
        },
      },
    };
  }

  get dialogUpdateStatus(): Options {
    return {
      title: this.$t('dialogOptions.confirmation').toString(),
      text: 'Weet je zeker dat je deze invoice wilt annuleren?',
      type: 'warning',
      buttons: {
        confirm: {
          text: 'annuleer',
          action: () => {
            if (!this.invoiceStatus || !this.order.id) {
              return;
            }

            const payload = {
              orders: [this.order.id],
              new_status: this.invoiceStatus,
            }

            new Order()
              .order(this.order.id)
              .updateStatusType(payload, 'status')
              .then(() => {
                this.selectedStatus = this.invoiceStatus;
                this.$store.dispatch('openDialog', this.dialogStatusOptions);
                this.isEditingOrder = false;
                this.invoiceStatus = '';
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
                this.invoiceStatus = '';
                this.isEditingOrder = false;
              })
          },
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
        },
      },
    };
  }

  get updateNoteDialogOptions(): Options {
    return {
      title: this.$t('dialogOptions.confirmation').toString(),
      text: 'Weet je zeker dat je deze personal note wilt updaten?',
      type: 'warning',
      buttons: {
        confirm: {
          text: this.$t('dialogOptions.button.ok').toString(),
          action: () => {
            if (!this.note || !this.order) {
              return;
            }

            new Order()
              .replacePersonalNote((this.order.id as string), this.note.id)
              .then(() => {
                this.$store.dispatch('openDialog', this.dialogSuccessOptions);
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });
          },
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
          action: () => {
            this.note = null;
          },
        },
      },
    };
  }

  get deleteDialogOptions(): Options {
    return {
      title: this.$t('dialogOptions.confirmation').toString(),
      text: 'Weet je zeker dat je deze invoice wilt verwijderen?',
      type: 'warning',
      buttons: {
        confirm: {
          text: this.$t('dialogOptions.button.delete').toString(),
          action: () => {
            if (!this.orderInvoices) {
              return;
            }

            const payload = {
              next_invoice_id: this.orderInvoices[0].next_invoice_id,
            }

            new Order()
              .deleteInvoice(this.order.id as string, payload)
              .then(() => {
                this.invoiceStatus = 'verwijderd';
                this.$store.dispatch('openDialog', this.invoiceStatusOption);
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });
          },
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
        },
      },
    };
  }

  get labelCreationDelayedOrFailedOption(): Options {
    return {
      title: `Label vertraagd of onverwerkbaar`,
      text: `Aanmaken van Label duurt lang of is momenteel niet mogelijk, probeer de pagina te verversen en/of probeer het later nog eens`,
      type: 'success',
      buttons: {
        confirm: {
          text: 'Oke',
          action: () => {
            this.$store.dispatch('closeDialog');
            this.initialize();
          }
        },
      },
    };
  }

  get canEdit() {
    if (!this.user) {
      return false;
    }

    return this.user.hasRole([RoleEnum.STYLIST, RoleEnum.ADMINISTRATOR, RoleEnum.SUPPORT])
  }

  get isAdmin() {
    if (!this.user) {
      return false;
    }

    return this.user.hasRole([RoleEnum.ADMINISTRATOR])
  }

  get orderItemEvaluationOptions(): OrderItemEvaluationOptions {
    return {
      overal: [
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.keep')}`,
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.return')}`,
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.exchange')}`,
      ],
      size: [
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.good')}`,
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.tooLarge')}`,
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.tooSmall')}`,
      ],
      fit: [
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.good')}`,
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.tooWide')}`,
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.tooTight')}`,
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.tooLong')}`,
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.tooShort')}`,
      ],
      style: [
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.good')}`,
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.tooSimple')}`,
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.tooBusy')}`,
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.tooSolid')}`,
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.tooYoung')}`,
      ],
      other: [
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.good')}`,
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.colorNotNice')}`,
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.printNotNice')}`,
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.tooExpensive')}`,
        `${this.$tc('orders.orderDetail.orderedItemsEvaluation.alreadyHaveSomethingSimilar')}`,
      ],
    };
  }

  protected formattedEventDate(date: string) {
    if (!date) {
      return null;
    }
    return formatDate(date);
  }

  protected dateTime(date: string) {
    if (date === '0000-00-00 00:00:00') {
      return 'N/A'
    } else {
      return DateTime.fromSQL(date).toFormat('dd-LL-yyyy hh:mm');
    }
  }

  get user(): User {
    return this.$store.state.Auth;
  }

  protected get isStylist() {
    return !!this.user && !!this.user.hasRole([RoleEnum.STYLIST], false);
  }

  public get statusOptions(): StatusOptions[] {
    return sortObjectsArrayByProperty(OrderStatusOptions.statusOptions, "value");
  }

  @Watch('isEditingOrder')
  @Watch('isEditingEvaluation')
  protected editingChanged(isEditing: boolean) {
    if (isEditing) {
      this.orderClone = cloneDeep(this.order);
    }
  }

  @Watch('activeTab')
  protected activeTabChanged() {

  }
}



interface OrderItemsMap {
  [key: string]: OrderItemMap;
}

interface OrderItemMap {
  fit: string[];
  style: string[];
  other: string[];
}

interface OrderItemEvaluationOptions {
  overal: string[];
  size: string[];
  fit: string[];
  style: string[];
  other: string[];
}
